import React from 'react'
import { Box, Typography } from '@mui/material'
import { keyframes } from '@emotion/react'
import BorkuMark from '../../assets/img/borku-mark.png'

// The "B" flips like a coin (with a short rest facing the viewer) while an
// orange arc sweeps around it and a soft glow breathes underneath.
// public/index.html carries a plain-CSS copy of this for the first paint, before
// React has loaded; keep the two in step if the look changes.
const flip = keyframes`
  0%   { transform: perspective(400px) rotateY(0deg) scale(1); }
  35%  { transform: perspective(400px) rotateY(180deg) scale(0.92); }
  70%  { transform: perspective(400px) rotateY(360deg) scale(1); }
  100% { transform: perspective(400px) rotateY(360deg) scale(1); }
`
const spin = keyframes`
  to { transform: rotate(360deg); }
`
const breathe = keyframes`
  0%, 100% { opacity: 0.35; transform: scale(0.85); }
  50%      { opacity: 0.7;  transform: scale(1); }
`
const dots = keyframes`
  0%, 80%, 100% { opacity: 0.25; }
  40%           { opacity: 1; }
`

function BrandLoader({ size = 88, label, fullScreen = false, sx }) {
  const ring = Math.max(3, Math.round(size / 22))
  return (
    <Box
      role="status"
      aria-live="polite"
      aria-label={label || 'Loading'}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 2,
        py: fullScreen ? 0 : 6,
        width: '100%',
        ...(fullScreen && { height: '100vh', bgcolor: '#F6F7F9' }),
        ...sx
      }}>
      <Box sx={{ position: 'relative', width: size, height: size }}>
        {/* glow */}
        <Box
          sx={{
            position: 'absolute',
            inset: '12%',
            borderRadius: '50%',
            background:
              'radial-gradient(circle, rgba(244,118,39,0.45) 0%, rgba(244,118,39,0) 70%)',
            animation: `${breathe} 1.8s ease-in-out infinite`
          }}
        />
        {/* track + sweeping arc */}
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            borderRadius: '50%',
            border: `${ring}px solid rgba(244,118,39,0.14)`
          }}
        />
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            borderRadius: '50%',
            background:
              'conic-gradient(from 0deg, rgba(244,118,39,0) 0deg, rgba(244,118,39,0) 200deg, #F47627 340deg, #F47627 360deg)',
            WebkitMask: `radial-gradient(farthest-side, transparent calc(100% - ${ring}px), #000 calc(100% - ${ring}px))`,
            mask: `radial-gradient(farthest-side, transparent calc(100% - ${ring}px), #000 calc(100% - ${ring}px))`,
            animation: `${spin} 1.1s linear infinite`
          }}
        />
        {/* the B */}
        <Box
          component="img"
          src={BorkuMark}
          alt=""
          sx={{
            position: 'absolute',
            inset: '18%',
            width: '64%',
            height: '64%',
            objectFit: 'contain',
            filter: 'drop-shadow(0 6px 10px rgba(244,118,39,0.35))',
            animation: `${flip} 1.8s cubic-bezier(.65,0,.35,1) infinite`
          }}
        />
      </Box>
      {label !== null ? (
        <Typography
          sx={{
            fontSize: 13,
            fontWeight: 600,
            color: 'text.disabled',
            letterSpacing: '0.02em',
            '& span': { animation: `${dots} 1.4s infinite` },
            '& span:nth-of-type(2)': { animationDelay: '0.2s' },
            '& span:nth-of-type(3)': { animationDelay: '0.4s' }
          }}>
          {label || 'Loading'}
          <span>.</span>
          <span>.</span>
          <span>.</span>
        </Typography>
      ) : null}
    </Box>
  )
}

export default BrandLoader
